import React, { useState } from 'react'
import Lottie from 'react-lottie'
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack'
import { Close } from '@mui/icons-material'
import { Box, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import { DialogProps } from '@mui/material/Dialog/Dialog'
import loadingAnimation from 'src/assets/lottie-files/loading.json'

export interface PreviewDialogProps extends DialogProps {
  /**
   * If true, the component is shown.
   * @default: false
   */
  open: boolean
  /**
   * If `close` icon clicked, will call this function
   */
  onClose: () => void
  /**
   * PDF's src
   */
  src?: string
  /**
   * If no `src`, display the lottie loading animation
   */
  loadingLottie?: string
  /**
   * Loading PDF's text
   * @default Loading PDF...
   */
  loadingPdf?: string
}

const DocumentPreviewDialog: React.FC<PreviewDialogProps> = ({
  open,
  onClose,
  src,
  loadingLottie = JSON.stringify(loadingAnimation),
  loadingPdf = 'Loading PDF...',
  ...rest
}) => {
  const [numPages, setNumPages] = useState(0)

  return (
    <Dialog
      data-element="document-preview-dialog"
      {...rest}
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="md"
      PaperProps={{
        sx: {
          width: '90%',
          height: 'calc(100% - 64px)'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1} />
          <Box>
            <IconButton data-element="document-preview-dialog-close-button" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          overflowX: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            textAlign: 'center',
            canvas: {
              width: '100%!important',
              height: 'auto!important'
            }
          }}
        >
          {src ? (
            <Document file={src} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={loadingPdf}>
              {Array.from(Array(numPages).keys()).map(page => (
                <Page key={page} loading={loadingPdf} scale={1} pageNumber={page + 1} />
              ))}
            </Document>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                marginTop: '-60px',
                left: '50%',
                marginLeft: '-60px'
              }}
            >
              <Lottie
                height={120}
                width={120}
                options={{
                  autoplay: true,
                  animationData: JSON.parse(loadingLottie),
                  loop: true,
                  rendererSettings: {
                    preserveAspectRatio: 'xMidYMid slice'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentPreviewDialog
